/**
 * @vibrant/models — shared model definitions for web and mobile.
 *
 * This package contains:
 *  - trends/       Trend feature DTOs, provider interface, goal utilities,
 *                  biomarker config, and portable constants.
 *  - member-profile-editor  Shared mobile/web DTOs for the profile editor flow.
 *  - mobile-records        Shared DTOs for the mobile medications/supplements viewer.
 *  - mobile-version        Native marketing-version comparison for the minimum-version gate.
 *  - wearables/    LOINC registry, wearable identifiers/source helpers, and the
 *                  canonical shared VitalObservation/WearableDevice models.
 *  - fhir/         Shared FHIR base class and Medplum model helpers used by
 *                  both web and mobile write models.
 *  - beta-affordance  Shared strings for BETA chip explainer copy (web + mobile).
 *
 * Web-specific write models live in apps/web/models/; mobile write models
 * live in apps/mobile/models/.
 */

// ---------------------------------------------------------------------------
// Product UI: BETA affordance copy
// ---------------------------------------------------------------------------
export * from "./beta-affordance";
export * from "./document/clinical-note-content";
export * from "./document/clinical-note-schema-contract";
// ---------------------------------------------------------------------------
// Document: shared extension URL constants
// ---------------------------------------------------------------------------
export * from "./document/extensions";
// ---------------------------------------------------------------------------
// FHIR: shared writable model base class
// ---------------------------------------------------------------------------
export * from "./fhir/FhirResource";
// ---------------------------------------------------------------------------
// FHIR: internal-note security label (NOPAT) shared by web, mobile, and the patient policy
// ---------------------------------------------------------------------------
export * from "./fhir/internal-note-security-label";
export * from "./fhir/loinc-code";
export * from "./fhir/telemetry";
// ---------------------------------------------------------------------------
// Mobile/Web shared API DTOs
// ---------------------------------------------------------------------------
export * from "./member-profile-editor";
export * from "./mobile-records";
export * from "./mobile-version";
// ---------------------------------------------------------------------------
// Parser review / patient reconciliation: shared cross-platform contract
// ---------------------------------------------------------------------------
export * from "./parser-review";
// ---------------------------------------------------------------------------
// Tasks: patient status command contract (web route + mobile client)
// ---------------------------------------------------------------------------
export * from "./task-status-contract";
export * from "./trends/biomarker-categorization";
export * from "./trends/biomarker-category-map";
export * from "./trends/biomarker-config";
// ---------------------------------------------------------------------------
// Trends: Biomarker configuration (BIOMARKER_CONFIG, FUNCTIONAL_SYSTEMS, etc.)
// ---------------------------------------------------------------------------
export * from "./trends/biomarker-content";
export * from "./trends/biomarker-counts";
export * from "./trends/collapse-duplicate-rows";
// ---------------------------------------------------------------------------
// Trends: Portable constants (categories, LOINC shortcuts, date helpers)
// ---------------------------------------------------------------------------
export * from "./trends/constants";
// ---------------------------------------------------------------------------
// Trends: Goal utilities (pure FHIR Goal helpers, no Medplum client)
// ---------------------------------------------------------------------------
export * from "./trends/goal-utils";
// ---------------------------------------------------------------------------
// Trends: DTOs
// ---------------------------------------------------------------------------
export * from "./trends/observation-source";
// ---------------------------------------------------------------------------
// Trends: canonical clinician-pin ↔ biomarker matching (patient web + mobile)
// ---------------------------------------------------------------------------
export * from "./trends/pin-loinc-match";
// ---------------------------------------------------------------------------
// Trends: Provider interface (JSON-safe)
// ---------------------------------------------------------------------------
export * from "./trends/provider-interface";
export * from "./trends/types";
// ---------------------------------------------------------------------------
// Trends: display-unit conversion + formatting (client-safe, web + mobile)
// ---------------------------------------------------------------------------
export * from "./trends/unit-display";
export * from "./wearables/loinc";
export * from "./wearables/timezone-offset";
// ---------------------------------------------------------------------------
// Wearables: shared models, identifiers, and LOINC metadata
// ---------------------------------------------------------------------------
export * from "./wearables/types";
export * from "./wearables/VitalObservation";
export * from "./wearables/WearableDevice";
